// Component
import Artists from 'components/track/artists'

// Types
import { Genre, DiscoverEntityType, NormalizedDiscoverEntity, State, Label, Collection, Artist, Track, Song } from 'types'

const normalizeArtist = (entity: Artist) => ({
  title: entity.name,
  description: entity.bio,
  url: `/artist/${entity.slug}`,
  image: typeof entity.image === 'string' ? entity.image : entity.image?.uri,
  slug: entity.slug,
})

const normalizeCollection = (entity: Collection): NormalizedDiscoverEntity => ({
  title: entity.title,
  description: entity.description || entity.owner_name,
  url: `/collection/${entity.id}`,
  image: entity.covers,
})

const normalizeGenre = (entity: Genre): NormalizedDiscoverEntity => ({
  title: entity?.name,
  description: entity?.description,
  url: `/genre/${entity?.slug}`,
  image: typeof entity.image === 'string' ? entity?.image : '',
})

const normalizeLabel = (entity: Label): NormalizedDiscoverEntity => ({
  title: entity.name,
  description: entity.description,
  url: entity.slug ? `/label/${entity.slug}` : `/tracks/?labels=${entity.id}`,
  image: entity.cover || undefined,
  slug: entity.slug,
})

const normalizeTrack = (entity: Track, state: State): NormalizedDiscoverEntity => {
  const song = state.entities.songs[String(entity.song)]
  const isStem = entity.types.some(type => type !== 17)
  return {
    title: `${song.title}${isStem ? ` - ${entity.name}` : ''}`,
    description: <Artists artists={song.artists} genres={song.genres} />,
    url: `/song/${song.slug}/${isStem ? `#${entity.slug}` : ''}`,
    image: song.cover,
    trackId: entity.id,
    songId: entity.song,
    slug: song.slug,
  }
}

const normalizeSong = (entity: Song): NormalizedDiscoverEntity => ({
  title: `${entity.title}`,
  description: <Artists artists={entity.artists} genres={entity.genres} />,
  url: `/song/${entity.slug}/`,
  image: entity.cover,
})

export const selectEntity = (state: State, type: DiscoverEntityType, id: number | string): NormalizedDiscoverEntity | null => {
  switch (type) {
    case 'label': {
      const entity = state.entities.labels[String(id)]
      return !entity ? null : normalizeLabel(entity)
    }
    case 'collection': {
      const entity = state.entities.collections[String(id)]
      return !entity ? null : normalizeCollection(entity)
    }
    case 'artist': {
      const entity = state.entities.artists[String(id)]
      return !entity ? null : normalizeArtist(entity)
    }
    case 'genre': {
      const entity = state.entities.genres[String(id)]
      return !entity ? null : normalizeGenre(entity)
    }
    case 'track': {
      const entity = state.entities.tracks[String(id)]
      return !entity ? null : normalizeTrack(entity, state)
    }
    case 'song': {
      const entity = state.entities.songs[String(id)]
      return !entity ? null : normalizeSong(entity)
    }
    default:
      return null
  }
}
